import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Button, Chip, Link } from '@mui/material';
import SearchableSelect from '../common/SearchableSelect';
import {
    SegmentTrialBalanceResponse,
    SegmentTrialBalanceBlock,
    Segment,
} from '../../interfaces/accounting.interface';
import { AccountService } from '../../services/account.service';
import { SegmentService } from '../../services/segment.service';
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';
import { toLocalIsoDate, firstOfYearLocalIso } from '../../utils/dateUtils';

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #ddd',
    fontWeight: 600,
};

const tableCellStyle = {
    padding: '10px 12px',
    borderBottom: '1px solid #eee',
};

const TYPE_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning'> = {
    ASSET: 'primary',
    LIABILITY: 'warning',
    EQUITY: 'secondary',
    REVENUE: 'success',
    EXPENSE: 'default',
};

const formatAmount = (value: number) =>
    value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const today = new Date();
const firstOfYear = firstOfYearLocalIso(today);
const todayStr = toLocalIsoDate(today);

const SegmentTrialBalance: React.FC = () => {
    const toast = useToast();
    const [report, setReport] = useState<SegmentTrialBalanceResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [from, setFrom] = useState(firstOfYear);
    const [to, setTo] = useState(todayStr);
    const [segments, setSegments] = useState<Segment[]>([]);
    const [selectedSegmentId, setSelectedSegmentId] = useState<string>('');

    useEffect(() => {
        const loadSegments = async () => {
            try {
                const page = await SegmentService.getByInstitution();
                setSegments(page.content ?? []);
            } catch (err) {
                toast.showApiError(err, 'Failed to load segments');
            }
        };
        loadSegments();
    }, []);

    const fetchReport = async () => {
        try {
            setLoading(true);
            const segmentId = selectedSegmentId || undefined;
            const data = await AccountService.getSegmentTrialBalance(from, to, segmentId);
            setReport(data);
        } catch (err) {
            toast.showApiError(err, 'Failed to load segment trial balance');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const hasData = report && report.segments.length > 0;

    return (
        <div data-help-page="accounting.segmentTrialBalance.page" style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' , flexWrap: 'wrap', gap: '8px' }}>
                <h2 style={{ margin: 0 }}>Segment Trial Balance <small style={{ fontWeight: 400, color: '#666' }}>(pre-closing: year-end closing entries excluded)</small></h2>
            </div>

            {/* Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
                <label data-help="accounting.segmentTrialBalance.from" style={{ fontWeight: 500 }}>
                    From:&nbsp;
                    <input
                        type="date"
                        value={from}
                        onChange={e => setFrom(e.target.value)}
                        style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                </label>
                <label data-help="accounting.segmentTrialBalance.to" style={{ fontWeight: 500 }}>
                    To:&nbsp;
                    <input
                        type="date"
                        value={to}
                        onChange={e => setTo(e.target.value)}
                        style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                </label>
                <SearchableSelect data-help="accounting.segmentTrialBalance.segment"
                    options={segments}
                    value={selectedSegmentId}
                    onChange={(id) => setSelectedSegmentId(id)}
                    getOptionLabel={(seg) => `${seg.code} — ${seg.name}`}
                    label="Segment"
                    placeholder="All Segments"
                    sx={{ flex: '1 1 200px', maxWidth: 300 }}
                />
                <Button data-help="accounting.segmentTrialBalance.apply" variant="contained" onClick={fetchReport} disabled={loading}>
                    {loading ? 'Loading...' : 'Apply'}
                </Button>
            </div>

            {/* Loading state */}
            {loading && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Loading...</div>
            )}

            {/* Empty state */}
            {!loading && !hasData && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                    No posted entries in the selected period.
                </div>
            )}

            {/*
              * Report body in ONE scroll box. Unlike the other two segment reports, the grand-total
              * footer here has three numeric columns (Debits, Credits, Net Balance) and no header
              * row of its own — it is only readable against the segment tables' headings. Separate
              * boxes would let it scroll independently and put a debit figure under "Credits".
              */}
            {!loading && hasData && (
            <ScrollableTable>
            {report!.segments.map((block: SegmentTrialBalanceBlock) => (
                <div key={block.segmentId ?? 'unassigned'} style={{ marginBottom: '32px' }}>
                    {/* Segment header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <span style={{ fontWeight: 700, fontSize: '1rem' }}>
                            {block.segmentCode ? `${block.segmentCode} — ` : ''}{block.segmentName}
                        </span>
                        <Chip
                            label={block.balanced ? 'Balanced' : 'Unbalanced'}
                            size="small"
                            color={block.balanced ? 'success' : 'error'}
                        />
                    </div>

                    {/* Segment table — the per-block total row is inside this same table */}
                    <table data-help-section="accounting.segmentTrialBalance.table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                        <thead>
                            <tr>
                                <th data-help="accounting.segmentTrialBalance.col.code" style={tableHeaderStyle}>Code</th>
                                <th data-help="accounting.segmentTrialBalance.col.name" style={tableHeaderStyle}>Account Name</th>
                                <th data-help="accounting.segmentTrialBalance.col.type" style={tableHeaderStyle}>Type</th>
                                <th data-help="accounting.segmentTrialBalance.col.debits" style={{ ...tableHeaderStyle, textAlign: 'right' as const }}>Debits</th>
                                <th data-help="accounting.segmentTrialBalance.col.credits" style={{ ...tableHeaderStyle, textAlign: 'right' as const }}>Credits</th>
                                <th data-help="accounting.segmentTrialBalance.col.net" style={{ ...tableHeaderStyle, textAlign: 'right' as const }}>Net Balance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {block.lines.map((line, i) => (
                                <tr key={line.accountId} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                                    <td style={tableCellStyle}>{line.accountCode}</td>
                                    <td style={tableCellStyle}>
                                        <Link
                                            component={RouterLink}
                                            to={`/accounting/accounts/${line.accountId}/ledger?from=${report!.fromDate}&to=${report!.toDate}${block.segmentId ? `&segmentId=${block.segmentId}` : ''}`}
                                            underline="hover"
                                            color="inherit"
                                        >
                                            {line.accountName}
                                        </Link>
                                    </td>
                                    <td style={tableCellStyle}>
                                        <Chip
                                            label={line.accountType}
                                            size="small"
                                            color={TYPE_COLORS[line.accountType] ?? 'default'}
                                        />
                                    </td>
                                    <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(line.totalDebits)}</td>
                                    <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(line.totalCredits)}</td>
                                    <td style={{
                                        ...tableCellStyle,
                                        textAlign: 'right',
                                        fontWeight: 500,
                                        color: line.netBalance >= 0 ? '#2e7d32' : '#c62828',
                                    }}>
                                        {formatAmount(line.netBalance)}
                                    </td>
                                </tr>
                            ))}
                            {/* Per-block totals row */}
                            <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 700, borderTop: '2px solid #aaa' }}>
                                <td style={tableCellStyle} colSpan={3}>Segment Total</td>
                                <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(block.totalDebits)}</td>
                                <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(block.totalCredits)}</td>
                                <td style={tableCellStyle} />
                            </tr>
                        </tbody>
                    </table>
                </div>
            ))}

            {/* Grand total footer — same box as the blocks above, see the note there */}
                <div style={{ marginTop: '8px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                        <tbody>
                            <tr style={{ backgroundColor: '#e8eaf6', fontWeight: 700, borderTop: '3px solid #7986cb' }}>
                                <td style={tableCellStyle} colSpan={3}>
                                    <span style={{ marginRight: '12px' }}>Grand Total</span>
                                    <Chip
                                        label={report!.balanced ? 'Balanced' : 'Unbalanced'}
                                        size="small"
                                        color={report!.balanced ? 'success' : 'error'}
                                    />
                                </td>
                                <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(report!.grandTotalDebits)}</td>
                                <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(report!.grandTotalCredits)}</td>
                                <td style={tableCellStyle} />
                            </tr>
                        </tbody>
                    </table>
                </div>
            </ScrollableTable>
            )}
        </div>
    );
};

export default SegmentTrialBalance;
